
import React, { useState } from 'react';
import { supabaseAuth } from '../../lib/supabase';

interface LoginPageProps {
  onLoginSuccess: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRegister, setIsRegister] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isRegister) {
        const { error } = await supabaseAuth.signUp({ email, password });
        if (error) throw error;
        setError("Verification protocol initialized. Check email.");
      } else {
        const { error } = await supabaseAuth.signInWithPassword({ email, password });
        if (error) throw error;
        onLoginSuccess();
      }
    } catch (err: any) {
      if (err.message === "Invalid login credentials") {
        setError("Access Denied: Incorrect credentials provided.");
      } else {
        setError(err.message || "Authentication failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-charcoal font-sans text-white">
       {/* Branding Header */}
       <header className="w-full max-w-7xl mx-auto px-8 py-10 flex items-center justify-between border-b border-white/5">
          {!logoError ? (
            <img 
              src="https://files.catbox.moe/w8074t.png" 
              alt="EventPulse" 
              className="h-12 w-auto object-contain"
              onError={() => setLogoError(true)}
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center text-charcoal shadow-[0_0_15px_rgba(139,62,255,0.3)]">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
               </div>
               <div className="flex flex-col justify-center">
                 <div className="text-xl font-black tracking-tighter italic uppercase leading-none text-white">EventPulse</div>
               </div>
            </div>
          )}
          <div className="hidden md:flex items-center gap-4">
             <span className="text-[10px] font-bold text-muted uppercase tracking-[0.3em]">SECURE ACCESS PROTOCOL</span>
          </div>
       </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="text-center space-y-6">
            <div className="text-xs font-bold text-accent uppercase tracking-widest">
              {isRegister ? 'Initialization' : 'Authentication'}
            </div>
            <h1 className="text-5xl font-black tracking-tight leading-none">
              {isRegister ? 'Join the System.' : 'Access Platform.'}
            </h1>
            <p className="text-base text-muted max-w-[320px] mx-auto leading-relaxed">
              Secure gateway to internal event intelligence and automation tools.
            </p>
          </div>

          <form onSubmit={handleAuth} className="space-y-6">
            <div className="space-y-5">
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Email Identifier</label>
                <input 
                  type="email" 
                  required 
                  placeholder="name@enterprise.com"
                  className="w-full bg-surface border border-white/10 rounded-sm px-6 py-4 text-sm text-white focus:outline-none focus:border-accent transition-all placeholder:text-white/10 font-medium"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Secure Passkey</label>
                <div className="relative">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    required 
                    placeholder="••••••••"
                    className="w-full bg-surface border border-white/10 rounded-sm px-6 py-4 text-sm text-white focus:outline-none focus:border-accent transition-all placeholder:text-white/10 font-medium pr-12"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-white transition-colors"
                    title={showPassword ? "Hide Passkey" : "Show Passkey"}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className={`p-4 rounded-sm text-xs font-bold border animate-in fade-in slide-in-from-top-1 ${error.includes("email") ? 'bg-accent/10 border-accent/20 text-accent' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-white text-charcoal font-black py-5 rounded-sm shadow-2xl hover:bg-white/90 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 uppercase text-xs tracking-widest"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-charcoal/20 border-t-charcoal rounded-full animate-spin"></div>
              ) : null}
              {loading ? 'Verifying...' : isRegister ? 'Initialize Access' : 'Authenticate Access'}
            </button>
          </form>

          <div className="text-center pt-2">
            <button 
              onClick={() => setIsRegister(!isRegister)}
              className="text-[10px] font-black text-muted hover:text-white transition-colors uppercase tracking-widest"
            >
              {isRegister ? '← Back to Authentication' : 'Request Access Node'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
